import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { useSession } from '@/hooks/useSession';
import { PcRecommendedCallout } from '@/components/PcRecommendedCallout';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { PaginationBar } from '@/components/ui/PaginationBar';
import {
  inputClass,
  labelClass,
  tableWrapClass,
  tableClass,
  theadClass,
  thClass,
  inlineErrorClass,
} from '@/components/ui/inputStyles';

const PER_PAGE = 20;

type Row = {
  id: number;
  username: string;
  user_id: number | null;
  role: string | null;
  ok: boolean;
  fail_reason: string | null;
  remote_ip: string | null;
  user_agent: string | null;
  created_at: string;
};

type ListResp = {
  ok: boolean;
  items: Row[];
  total: number;
  page: number;
  per_page: number;
};

function outcomeLabel(row: Row): string {
  if (row.ok) {
    return '成功';
  }
  const m: Record<string, string> = {
    bad_credentials: '用户名或密码错误',
    account_disabled: '账号已禁用',
    account_expired: '账号已过期',
  };
  const k = row.fail_reason ?? '';
  return m[k] || k || '失败';
}

export function LoginLogsPage() {
  const { capabilities, loading: sessLoading } = useSession();
  const [items, setItems] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [appliedQ, setAppliedQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [qInput, setQInput] = useState('');

  const fetchList = useCallback(async (nextPage: number, q: string) => {
    setLoading(true);
    setErr(null);
    try {
      const r = await apiFetch<ListResp>('list_login_logs', {
        method: 'GET',
        query: { page: nextPage, per_page: PER_PAGE, ...(q !== '' ? { q } : {}) },
      });
      if (r.ok) {
        setPage(nextPage);
        setAppliedQ(q);
        setItems(Array.isArray(r.items) ? r.items : []);
        setTotal(typeof r.total === 'number' ? r.total : 0);
      } else {
        setErr('加载失败');
        setItems([]);
        setTotal(0);
      }
    } catch (e) {
      if (e instanceof Error && e.message === 'SESSION_EXPIRED') {
        setErr(null);
        return;
      }
      setErr(e instanceof Error ? e.message : '错误');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (sessLoading || !capabilities.manage_users) return;
    void fetchList(1, '');
  }, [sessLoading, capabilities.manage_users, fetchList]);

  function runSearch() {
    void fetchList(1, qInput.trim());
  }

  if (!sessLoading && !capabilities.manage_users) {
    return <Navigate to="/dashboard" replace />;
  }

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <div className="space-y-6">
      <PageHeader
        title="系统登录日志"
        description="记录后台登录成功与失败尝试（含来源 IP、浏览器标识）。仅系统管理员可查看。"
      />

      <PcRecommendedCallout>
        登录审计表列多、适合横向滚动与对照排查，建议在电脑浏览器中查看与搜索。
      </PcRecommendedCallout>

      <Card title="筛选">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <label className={labelClass + ' flex-1'}>
            <span>关键词（用户名 / 用户 ID / 失败原因 / IP）</span>
            <input
              className={inputClass}
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') runSearch();
              }}
              placeholder="可选，回车搜索"
            />
          </label>
          <Button type="button" variant="secondary" onClick={runSearch} disabled={loading}>
            搜索
          </Button>
        </div>
      </Card>

      {err ? <div className={inlineErrorClass}>{err}</div> : null}

      <Card title="记录列表">
        {loading ? (
          <Spinner label="加载中" />
        ) : items.length === 0 ? (
          <EmptyState>暂无记录。有登录尝试后会显示在此。</EmptyState>
        ) : (
          <>
            <div className={tableWrapClass}>
              <table className={tableClass}>
                <thead className={theadClass}>
                  <tr>
                    <th className={thClass}>时间</th>
                    <th className={thClass}>结果</th>
                    <th className={thClass}>用户名</th>
                    <th className={thClass}>用户 ID</th>
                    <th className={thClass}>角色</th>
                    <th className={thClass}>IP</th>
                    <th className={thClass}>浏览器</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {items.map((row) => (
                    <tr key={row.id} className="text-sm text-slate-800">
                      <td className="whitespace-nowrap px-4 py-3 text-slate-600">{row.created_at}</td>
                      <td className="px-4 py-3">
                        <span
                          className={
                            row.ok
                              ? 'font-medium text-emerald-700'
                              : 'font-medium text-rose-700'
                          }
                        >
                          {outcomeLabel(row)}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium">{row.username}</td>
                      <td className="px-4 py-3 text-slate-600">{row.user_id ?? '—'}</td>
                      <td className="px-4 py-3 text-slate-600">{row.role ?? '—'}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-600">{row.remote_ip ?? '—'}</td>
                      <td className="max-w-[200px] truncate px-4 py-3 text-xs text-slate-500" title={row.user_agent ?? ''}>
                        {row.user_agent ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationBar
              page={page}
              totalPages={totalPages}
              total={total}
              onPrev={() => void fetchList(Math.max(1, page - 1), appliedQ)}
              onNext={() => void fetchList(Math.min(totalPages, page + 1), appliedQ)}
              summary={total > 0 ? `共 ${total} 条，每页 ${PER_PAGE} 条` : undefined}
            />
          </>
        )}
      </Card>
    </div>
  );
}
